import ScheduledCoverageChart from "./ScheduledCoverageChart";
import { getStoreBusinessDate } from "../../../../utilities/storeTime";
import { getWeeklyActualMetrics } from "./weeklyActualMetrics";
import React, { useLayoutEffect, useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DailyMetricsCard2 from "./DailyMetricsCard2";
import DailyNoLaborModelMetricsCard, { getScheduleHourSummary } from "./DailyNoLaborModelMetricsCard";
import DailyChartCard2 from "./DailyChartCard2";
import WeekChartLegendCard from "./WeekChartLegendCard";
import VisualButtonOptions2 from "./VisualButtonOptions2";
import Tooltip from "@mui/material/Tooltip";

const day_nums = [0, 1, 2, 3, 4, 5, 6];

function FixedBottomMetricsBar({
  showDailyMetrics,
  toggleShowDailyMetrics,
  bottomCardType,
  setBottomCardType,
  checkedMetricsToggle,
  handleChangeMetricsToggle,
  props,
  get_date_info,
  calculate_letter,
  calculate_color,
  calculate_daily_budget_score,
  calculate_intraday_coverage_score,
  combine_data,
  sum_daily_sales,
  sum_daily_sales_up_to_current_day,
  open_shifts,
  windowWidth,
  actualCards = [],
  publishedCoverageShifts = [],
  actualHoursAvailable = true,
  publishedScheduledHours = 0,
  scheduledHoursByMemberDate = {},
}) {
  // Chart mode gets a taller cell so the intraday steps stay readable;
  // cards stay compact. Heights switch hard (no transition) so the grid's
  // bottom spacer never lags behind the fixed bar.
  const targetLabor = checkedMetricsToggle
    ? props.all_data.calculated_earned_labor
    : props.all_data.forecasted_labor;
  const dayInfos = day_nums.map(get_date_info);
  const summaryRoleTypes = props.roleType?.length < props.roleTypeOptions?.length ? props.roleType : undefined;
  const businessDate = props.storeBusinessDate || getStoreBusinessDate(props.storeTimezone, props.start_qhnum);
  const hasWeeklyPlannedModel = props.laborModelEnabled && props.all_data.forecasted_labor?.length > 0 &&
    dayInfos.every((info) => {
      if (info[12]?.length > 0) return true;
      const operatingHours = info[20]?.[0] || info[19]?.[0];
      const closed = operatingHours?.open_time === 0 && operatingHours?.close_time === 0;
      return closed && !(info[13] || []).some(row => Number(row.hours) > 0) &&
        getScheduleHourSummary(info[7], [], summaryRoleTypes).scheduled === 0;
    });
  const chartMode = bottomCardType === "chart";
  const anyModeledDay = props.laborModelEnabled && targetLabor?.length > 0;
  const weekDates = new Set(dayInfos.map((info) => info[2]));
  const weekActualCards = actualCards.filter((card) => weekDates.has(card.date));
  const weeklyActual = getWeeklyActualMetrics({
    days: dayInfos.map(info => {
      const operatingHours = info[20]?.[0] || info[19]?.[0];
      return {
        date: info[2],
        closed: operatingHours?.open_time === 0 && operatingHours?.close_time === 0,
        hasScheduledHours: Object.values(scheduledHoursByMemberDate).some(days => days[info[2]] > 0),
      };
    }),
    businessDate,
    earned: props.all_data.calculated_earned_labor,
    actual: props.all_data.actual_used_labor_vs_earned,
    cards: weekActualCards,
  });
  const hasWeeklyModel = checkedMetricsToggle
    ? props.laborModelEnabled && weeklyActual.hasTarget
    : hasWeeklyPlannedModel;
  const weeklyRows = rows => checkedMetricsToggle
    ? (rows || []).filter(row => weeklyActual.dates.has(row.date))
    : rows;
  const cardHeight = anyModeledDay ? (chartMode ? 150 : 100) : (chartMode ? 80 : 36) + (checkedMetricsToggle && actualHoursAvailable ? 32 : 0);
  const expandedHeight = cardHeight + 32;
  const visibleHeight = (showDailyMetrics ? expandedHeight : 32) + (windowWidth < 1024 ? 16 : 0);
  const barRef = useRef(null);

  useLayoutEffect(() => {
    const bar = barRef.current;
    const rootStyle = document.documentElement.style;
    const updateInset = () => rootStyle.setProperty(
      "--schedule-bottom-metrics-height", `${bar.getBoundingClientRect().height}px`
    );
    updateInset();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateInset);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      rootStyle.removeProperty("--schedule-bottom-metrics-height");
    };
  }, [showDailyMetrics, bottomCardType, windowWidth]);

  const renderWeeklyMetricsCard = (variant) => (
    <DailyMetricsCard2
      variant={variant}
      targetLaborAvailable={hasWeeklyModel}
      actualUsedHours={checkedMetricsToggle ? weeklyActual.actualHours : undefined}
      actualGradeAvailable={weeklyActual.gradeAvailable}
      actualComparisonAvailable={weeklyActual.actualComplete}
      fallbackUsedHours={checkedMetricsToggle ? weeklyActual.actualHours : getScheduleHourSummary(dayInfos.flatMap(info => info[7] || []), [], summaryRoleTypes).scheduled}
      weekly_or_daily="weekly"
      forecasted_labor={props.all_data.forecasted_labor}
      actual_schedule_labor={
        props.all_data.actual_schedule_labor_deployment
      }
      actual_schedule={props.all_data.actual_schedule}
      open_shifts={open_shifts}
      calculated_earned_labor={
        weeklyActual.earnedRows
      }
      actual_used_labor_vs_earned={
        weeklyActual.actualRows
      }
      grade_weights={props.all_data.grade_weights}
      agg_column={props.agg_column}
      day_in_past={weeklyActual.dates.size > 0}
      todays_date_yes={false}
      adjustments_chosen={props.adjustments_chosen}
      calculate_letter={calculate_letter}
      calculate_color={calculate_color}
      checkedMetricsToggle={checkedMetricsToggle}
      forecast_sales={sum_daily_sales(
        props.all_data.forecasted_sales_daily
      )}
      forecast_transactions={props.agg_column(
        props.filter_df_to_day(
          weeklyRows(props.all_data.forecasted_other_daily),
          "metric",
          "Transaction"
        ),
        "value"
      )}
      actual_transactions={props.agg_column(
        props.filter_df_to_day(
          weeklyRows(props.all_data.actual_other_daily),
          "metric",
          "Transaction"
        ),
        "value"
      )}
      forecast_sales_up_to_current_day={sum_daily_sales_up_to_current_day()}
      actual_sales={props.agg_column(
        weeklyRows(props.all_data.actual_sales_daily),
        "value"
      )}
      calculate_daily_budget_score={
        calculate_daily_budget_score
      }
      calculate_intraday_coverage_score={
        calculate_intraday_coverage_score
      }
      showLaborPercent={props.showLaborPercent}
      averageWageFromPreviousWeek={
        props.averageWageFromPreviousWeek
      }
      dailyLaborPercentData={weeklyRows(props.laborPercentData)}
      windowWidth={windowWidth}
      tplhEnabled={props.tplhEnabled}
      laborModelEnabled={props.laborModelEnabled}
    />
  );

  return (
    <>
      <div
        className="w-full"
        style={{ height: visibleHeight }}
      ></div>

      {/* Fixed Bottom Metrics Bar */}
      <div
        ref={barRef}
        className="fixed bottom-0 right-0 bg-slate-50 border-t border-gray-200 shadow-lg z-50"
        style={{ left: "calc(60px + var(--ops-dock-width, 0px))" }}
      >
        {/* Toggle Button */}
        <button
          aria-label={showDailyMetrics ? "Collapse schedule metrics" : "Expand schedule metrics"}
          aria-expanded={showDailyMetrics}
          onClick={toggleShowDailyMetrics}
          className="transition-colors duration-200 text-gray-500 hover:text-gray-700 absolute left-1 top-1"
        >
          {showDailyMetrics ? (
            <ExpandMoreIcon className="h-4 w-4" />
          ) : (
            <ExpandLessIcon className="h-4 w-4" />
          )}
        </button>

        {/* Expandable Content */}
        <div
          role="region"
          aria-label="Schedule metrics"
          tabIndex={windowWidth < 1024 ? 0 : undefined}
          style={{
            maxHeight: visibleHeight,
            overflowX: windowWidth < 1024 ? "auto" : undefined,
          }}
        >
          <div className="flex w-full px-5">
            {/* Weekly Metrics Column (matches employee column width) */}
            <div className={`${windowWidth <= 1279 ? "w-[180px]" : "w-[200px]"} min-w-[160px]`}>
              <div>
                {/* Conditionally render metrics cards only when expanded */}
                {showDailyMetrics && (
                  <>
                    {/* Fixed height container for metrics card */}
                    <div
                      className={`flex flex-col bg-slate-50 px-3 ${
                        chartMode ? "justify-start pt-1" : "justify-center"
                      }`}
                      style={{ height: cardHeight }}
                    >
                      {chartMode && hasWeeklyModel ? (
                        <WeekChartLegendCard
                          earnedToggle={checkedMetricsToggle}
                          header={renderWeeklyMetricsCard("header")}
                        />
                      ) : anyModeledDay ? (
                        <div className="w-full">
                          {renderWeeklyMetricsCard(chartMode ? "header" : undefined)}
                        </div>
                      ) : (
                        <DailyNoLaborModelMetricsCard
                          weekly_or_daily="weekly"
                          actualHoursAvailable={actualHoursAvailable}
                          publishedScheduledHours={publishedScheduledHours}
                          roleTypes={summaryRoleTypes}
                          actual_schedule={dayInfos.flatMap((info) => info[7] || [])}
                          actualCards={weekActualCards}
                          checkedMetricsToggle={checkedMetricsToggle}
                        />
                      )}
                    </div>
                  </>
                )}

                {showDailyMetrics && <hr className="w-full border-gray-200" />}
                <div className="h-8 flex items-center bg-slate-50 px-3">
                  <div
                    className="w-full flex gap-0.5 rounded-lg bg-slate-100 p-[2px]"
                    role="tablist"
                    aria-label="Planned or actual"
                  >
                    {[
                      { label: "Planned", checked: false },
                      { label: "Actual", checked: true },
                    ].filter(({ checked }) => !checked || actualHoursAvailable).map(({ label, checked }) => {
                      const active = !!checkedMetricsToggle === checked;
                      // Nothing has been worked yet, so selecting Actual would
                      // light up this tab while the planned schedule stayed on
                      // screen, editable, saying otherwise.
                      const unavailable =
                        checked && props.weekHasStartedForActuals === false;
                      return (
                        <Tooltip
                          key={label}
                          title={
                            unavailable ? "This week has not started yet" : ""
                          }
                        >
                          <span className="flex-1 inline-flex">
                            <button
                              type="button"
                              role="tab"
                              aria-selected={active}
                              disabled={unavailable}
                              onClick={() => {
                                if (!active && !unavailable) {
                                  handleChangeMetricsToggle({ target: { checked } });
                                }
                              }}
                              className={`flex-1 px-2.5 py-0.5 rounded-md text-xxs font-semibold transition-colors duration-150 ${
                                unavailable
                                  ? "text-slate-300 cursor-not-allowed"
                                  : active
                                  ? "bg-white text-violet-700 shadow-sm"
                                  : "text-slate-500 hover:text-slate-700"
                              }`}
                            >
                              {label}
                            </button>
                          </span>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Metrics Columns (matches day columns) */}
            {day_nums.map((d, i) => {
              const dateInfo = dayInfos[i];
              const [
                locked,
                date,
                date_str,
                date_dow,
                date_name,
                , // Store business date controls Actual visibility below.
                dow_name,
                day_shifts,
                day_request_off,
                dow_availability,
                edit_mode,
                ,
                forecasted_labor_day,
                actual_schedule_labor_day,
                calculated_earned_labor_day,
                actual_used_labor_vs_earned_day,
                adjustment_chosen,
                forecast_sales,
                actual_sales,
                dow_default_operating_hours,
                day_operating_hours,
                day_time_off_requests,
                historical_sales_context,
                day_other_store_shifts,
                used_day_shifts,
                adjustments_history,
                day_shifts_for_length
              ] = dateInfo;

              // Filter data for the current day
              const daily_weather = props.filter_df_to_day(
                props.all_data.weather,
                "date",
                date_str
              );

              const daily_events = props.filter_df_to_day(
                props.all_data.events,
                "date",
                date_str
              );

              const daily_local_events = props.filter_df_to_day(
                props.all_data.local_events,
                "date",
                date_str
              );

              const daily_forecast_exclude_date = props.filter_df_to_day(
                props.all_data.forecast_exclude_dates,
                "date",
                date_str
              );

              const daily_forecast_other = props.filter_df_to_day(
                props.all_data.forecasted_other_daily,
                "date",
                date_str
              );

              const daily_actual_other = props.filter_df_to_day(
                props.all_data.actual_other_daily,
                "date",
                date_str
              );

              const daily_labor_percent_data = props.filter_df_to_day(
                props.laborPercentData,
                "date",
                date_str
              );

              // Get day of week string
              const dayOfWeekStr = date.toLocaleDateString("en-us", {
                weekday: "short",
              });

              // Get daily data from objects
              const daily_batch_guidance = props.all_data.batch_guidance[date_str] || {};
              const daily_intraday_metrics = props.all_data.intraday_metrics[date_str] || {};
              const actual_intraday_metrics = props.all_data.actual_intraday_metrics[date_str] || {};

              // Calculate daily adjustment
              const daily_adjustment = (adjustment_chosen.length === 0
                ? 0
                : adjustment_chosen[0]["adjustment_percent_value"]) + 1;

              // Calculate forecast metrics
              const daily_forecast_transactions = props.agg_column(
                props.filter_df_to_day(
                  props.all_data.forecasted_transactions_daily,
                  "date",
                  date_str
                ),
                "value"
              ) * daily_adjustment;

              const daily_forecast_sales = daily_adjustment * forecast_sales;

              const daily_open_shifts = props.filter_df_to_day(
                open_shifts,
                "date",
                date_str
              );

              // Count unique employees working
              const day_num_employees_working = new Set(
                day_shifts_for_length?.filter(shift =>
                  shift.store_team_member !== null && shift.other_store === false
                )?.map(shift => shift.store_team_member)
              )?.size;

              const hasDailyModel = props.laborModelEnabled &&
                (checkedMetricsToggle ? calculated_earned_labor_day : forecasted_labor_day)?.length > 0;
              const renderDailyMetricsCard = (variant) => (
                                <DailyMetricsCard2
                                  variant={variant}
                                  targetLaborAvailable={hasDailyModel}
                                  fallbackUsedHours={checkedMetricsToggle ? (actual_used_labor_vs_earned_day?.length ? props.agg_column(actual_used_labor_vs_earned_day, "hours") : getScheduleHourSummary([], weekActualCards.filter(card => card.date === date_str)).actual) : getScheduleHourSummary(day_shifts, [], summaryRoleTypes).scheduled}
                                  forecasted_labor={forecasted_labor_day}
                                  actual_schedule_labor={actual_schedule_labor_day}
                                  actual_schedule={day_shifts_for_length}
                                  open_shifts={daily_open_shifts}
                                  calculated_earned_labor={
                                    calculated_earned_labor_day
                                  }
                                  actual_used_labor_vs_earned={
                                    actual_used_labor_vs_earned_day
                                  }
                                  grade_weights={props.all_data.grade_weights}
                                  agg_column={props.agg_column}
                                  edit_mode={edit_mode}
                                  day_in_past={date_str < businessDate}
                                  todays_date_yes={date_str === businessDate}
                                  calculate_letter={calculate_letter}
                                  calculate_color={calculate_color}
                                  checkedMetricsToggle={checkedMetricsToggle}
                                  forecast_sales={daily_forecast_sales}
                                  forecast_transactions={props.agg_column(
                                    props.filter_df_to_day(
                                      daily_forecast_other,
                                      "metric",
                                      "Transaction"
                                    ),
                                    "value"
                                  )}
                                  actual_transactions={props.agg_column(
                                    props.filter_df_to_day(
                                      daily_actual_other,
                                      "metric",
                                      "Transaction"
                                    ),
                                    "value"
                                  )}
                                  actual_sales={actual_sales}
                                  isLastCard={i >= day_nums.length - 2}
                                  calculate_daily_budget_score={
                                    calculate_daily_budget_score
                                  }
                                  calculate_intraday_coverage_score={
                                    calculate_intraday_coverage_score
                                  }
                                  day_of_week_str={dayOfWeekStr}
                                  daily_actual_other={daily_actual_other}
                                  daily_forecast_other={daily_forecast_other}
                                  daily_adjustment={daily_adjustment}
                                  showLaborPercent={props.showLaborPercent}
                                  averageWageFromPreviousWeek={
                                    props.averageWageFromPreviousWeek
                                  }
                                  dailyLaborPercentData={daily_labor_percent_data}
                                  tplhEnabled={props.tplhEnabled}
                                  laborModelEnabled={props.laborModelEnabled}
                                  windowWidth={windowWidth}
                                />
              );

              return (
                <div key={i} className={`flex-1 border-l border-gray-200 ${windowWidth < 1024 ? "min-w-[140px]" : "min-w-[100px]"}`}>
                  <div>
                    {/* Conditionally render metrics/chart cards only when expanded */}
                    {showDailyMetrics && (
                      <>
                        {/* Fixed height container for metrics/chart cards */}
                        <div
                          className={`flex flex-col bg-slate-50 px-3 ${
                            chartMode ? "justify-start pt-1" : "justify-center"
                          }`}
                          style={{ height: cardHeight }}
                        >
                          {!anyModeledDay ? (
                            <>
                              <DailyNoLaborModelMetricsCard
                                compact
                                actualHoursAvailable={actualHoursAvailable}
                                actual_schedule={day_shifts}
                                roleTypes={summaryRoleTypes}
                                publishedScheduledHours={Object.values(scheduledHoursByMemberDate).reduce((sum, days) => sum + (days[date_str] || 0), 0)}
                                actualCards={weekActualCards.filter((card) => card.date === date_str)}
                                checkedMetricsToggle={checkedMetricsToggle}
                                day_of_week_str={dayOfWeekStr}
                              />
                              {chartMode && <ScheduledCoverageChart
                                shifts={checkedMetricsToggle && actualHoursAvailable ? publishedCoverageShifts.filter(shift => shift.date === date_str) : day_shifts}
                                roleTypes={summaryRoleTypes}
                                defaultHours={dow_default_operating_hours}
                                dailyHours={day_operating_hours}
                              />}
                            </>
                          ) : !hasDailyModel || (checkedMetricsToggle && (date_str > businessDate || !actual_used_labor_vs_earned_day?.length)) ? (
                            <div className="w-full">{renderDailyMetricsCard(chartMode ? "header" : undefined)}</div>
                          ) : !chartMode ? (
                            <div className="w-full">{renderDailyMetricsCard()}</div>
                          ) : (
                            <div className="w-full pt-1">
                              {renderDailyMetricsCard("header")}
                            <DailyChartCard2
                              hideHeader
                              grade={{
                                letter: checkedMetricsToggle
                                  ? calculate_letter(
                                      actual_used_labor_vs_earned_day,
                                      calculated_earned_labor_day
                                    )
                                  : calculate_letter(
                                      actual_schedule_labor_day,
                                      forecasted_labor_day
                                    ),
                                color: checkedMetricsToggle
                                  ? calculate_color(
                                      actual_used_labor_vs_earned_day,
                                      calculated_earned_labor_day
                                    )
                                  : calculate_color(
                                      actual_schedule_labor_day,
                                      forecasted_labor_day
                                    ),
                              }}
                              forecasted_labor_day={forecasted_labor_day}
                              actual_schedule_labor_day={
                                actual_schedule_labor_day
                              }
                              calculated_earned_labor_day={
                                calculated_earned_labor_day
                              }
                              actual_used_labor_vs_earned_day={
                                actual_used_labor_vs_earned_day
                              }
                              actual_schedule={day_shifts}
                              agg_column={props.agg_column}
                              get_t_str={props.get_t_str}
                              combine_data={combine_data}
                              dow_default_operating_hours={
                                dow_default_operating_hours
                              }
                              day_operating_hours={day_operating_hours}
                              date_name={date_name}
                              earned_toggle={checkedMetricsToggle}
                              index={i}
                              windowWidth={windowWidth}
                              laborModelEnabled={props.laborModelEnabled}
                            />
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {showDailyMetrics && <hr className="w-full border-gray-200" />}
                  <div className="w-full h-8 flex items-center justify-center bg-slate-50">
                    <VisualButtonOptions2
                      laborLawEnabled={props.laborLawEnabled}
                      printHoursShownEnabled={props.printHoursShownEnabled}
                      openLiveAction={props.openLiveAction}
                      date_name={date_name}
                      date_str={date_str}
                      forecasted_labor_day={forecasted_labor_day}
                      store_team_member_weekly_info={
                        props.store_team_member_weekly_info
                      }
                      actual_schedule_labor_day={
                        actual_schedule_labor_day
                      }
                      calculated_earned_labor_day={
                        calculated_earned_labor_day
                      }
                      actual_used_labor_vs_earned_day={
                        actual_used_labor_vs_earned_day
                      }
                      actual_schedule={day_shifts}
                      agg_column={props.agg_column}
                      get_t_str={props.get_t_str}
                      combine_data={combine_data}
                      dow_default_operating_hours={
                        dow_default_operating_hours
                      }
                      day_operating_hours={day_operating_hours}
                      daily_batch_guidance={daily_batch_guidance}
                      daily_intraday_metrics={daily_intraday_metrics}
                      actual_intraday_metrics={actual_intraday_metrics}
                      forecast_sales={daily_forecast_sales}
                      calculate_daily_budget_score={
                        calculate_daily_budget_score
                      }
                      calculate_intraday_coverage_score={
                        calculate_intraday_coverage_score
                      }
                      calculate_letter={calculate_letter}
                      calculate_color={calculate_color}
                      daily_weather={daily_weather}
                      checkedMetricsToggle={checkedMetricsToggle}
                      role_types={props.all_data.role_types}
                      role_names={props.all_data.role_names}
                      businessWeekStartDayIndex={props.businessWeekStartDayIndex}
                      start_qhnum={props.start_qhnum}
                      schedule_day_end_qhnum={props.schedule_day_end_qhnum}
                      actual_used_schedule={used_day_shifts}
                      laborModelEnabled={anyModeledDay}
                      modelDataAvailable={hasDailyModel}
                      store_id={props.store_id}
                      surveys={props.all_data.surveys}
                      guestReviewsEnabled={props.guestReviewsEnabled}
                      liveLaborEnabled={props.liveLaborEnabled}
                      refreshActualsForDate={props.refreshActualsForDate}
                      pos_provider={props.pos_provider}
                      storeTimezone={props.storeTimezone}
                      dailyPlanEnabled={props.dailyPlanEnabled}
                      homeCompany={props.homeCompany}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default FixedBottomMetricsBar;
